import { useEffect, useRef, useState } from "react";
import { IconButton, Tooltip } from "@datapay-by-datacom/design-system";
import { FiEdit2 } from "react-icons/fi";
import s from "./InlineEditableField.module.css";

export interface InlineEditableFieldProps {
  label: string;
  value: string;
  onCommit: (value: string) => void;
  isDisabled?: boolean;
  isInvalid?: boolean;
  isEdited?: boolean;
  isRequired?: boolean;
  describedBy?: string;
}

/**
 * Compact display-first field editor. The pencil is revealed on hover or
 * keyboard focus, while fields requiring attention keep their affordance and
 * status visible. Enter/blur saves and Escape cancels.
 */
export function InlineEditableField({
  label,
  value,
  onCommit,
  isDisabled = false,
  isInvalid = false,
  isEdited = false,
  isRequired = false,
  describedBy,
}: InlineEditableFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const skipBlurRef = useRef(false);

  useEffect(() => {
    if (!isEditing) setDraft(value);
  }, [isEditing, value]);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  const draftIsInvalid = isInvalid || (isRequired && draft.trim() === "");

  const save = () => {
    if (draft !== value) onCommit(draft);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div
        className={`${s.editor} ${draftIsInvalid ? s.invalid : ""} ${
          !draftIsInvalid && isEdited ? s.edited : ""
        }`}
        data-invalid={draftIsInvalid || undefined}
      >
        <input
          ref={inputRef}
          className={s.input}
          aria-label={label}
          aria-required={isRequired || undefined}
          aria-invalid={draftIsInvalid || undefined}
          aria-describedby={describedBy}
          value={draft}
          size={Math.max(draft.length, 1)}
          disabled={isDisabled}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => {
            if (skipBlurRef.current) {
              skipBlurRef.current = false;
              return;
            }
            save();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              save();
            } else if (event.key === "Escape") {
              event.preventDefault();
              skipBlurRef.current = true;
              setDraft(value);
              setIsEditing(false);
            }
          }}
        />
        <span className={s.status}>
          {draftIsInvalid ? "Needs review" : isEdited ? "Edited" : ""}
        </span>
      </div>
    );
  }

  return (
    <div
      className={`${s.display} ${isInvalid ? s.invalid : ""} ${
        !isInvalid && isEdited ? s.edited : ""
      }`}
      data-invalid={isInvalid || undefined}
      role="group"
      aria-label={label}
      aria-invalid={isInvalid || undefined}
      aria-describedby={describedBy}
    >
      <span className={`${s.value} ${value.trim() === "" ? s.empty : ""}`}>
        {value.trim() === "" ? "—" : value}
      </span>
      {(isInvalid || isEdited) && (
        <span className={s.status}>
          {isInvalid ? "Needs review" : "Edited"}
        </span>
      )}
      <Tooltip
        text={`Edit ${label}`}
        placement="top"
        className={s.editTooltip}
      >
        <IconButton
          className={s.editButton}
          variant="ghost"
          size="small"
          aria-label={`Edit ${label}`}
          isDisabled={isDisabled}
          onPress={() => {
            skipBlurRef.current = false;
            setDraft(value);
            setIsEditing(true);
          }}
        >
          <FiEdit2 size={16} />
        </IconButton>
      </Tooltip>
    </div>
  );
}
